// @ts-nocheck
/**
 * Flash Message list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + DataTable + shadcn Dialog for view/edit). Per-row View button opens the
 * dialog; superadmin/edit-ACL users can flip into inline textarea edit and
 * POST to `${API_FLASH_MESSAGE_EDIT}/:id`. Preserved: client-side search
 * only on module_name.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Eye, Loader2, MessageSquare, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_FLASH_MESSAGE_LIST, API_FLASH_MESSAGE_EDIT } from '@/service/url';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const FlashMessageList = () => {
  const navigate = useNavigate();
  const [flashMessageList, setFlashMessageList] = useState<any[]>([]);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [selectedId, setSelectedId] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    if (user?.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
    fetchFlashMessage();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchFlashMessage = () => {
    setLoading(true);
    getApiCall(API_FLASH_MESSAGE_LIST)
      .then((resp: any) => {
        setFlashMessageList(resp || []);
        setOriginalDataList(resp || []);
      })
      .catch((err: any) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchValue) {
      setFlashMessageList(
        originalDataList.filter((data: any) =>
          data.module_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
        ),
      );
    } else {
      setFlashMessageList(originalDataList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'flash-message-list',
        data: flashMessageList,
        columns: [
          { header: 'Id', accessor: 'id' },
          { header: 'Module Name', accessor: 'module_name' },
          { header: 'Action Name', accessor: 'action' },
          { header: 'Type', accessor: 'type' },
          { header: 'Priority(Notes)', accessor: 'priority' },
          { header: 'Created Date', accessor: (row: any) => formatDate(row.created_at) },
          { header: 'Message', accessor: (row: any) => row.message || '-' },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleViewMessage = (rowData: any) => {
    setSelectedId(rowData.id);
    setSelectedMessage(rowData.message);
    setIsEditMode(false);
    setViewModalVisible(true);
  };

  const handleUpdateMessage = async () => {
    try {
      setUpdating(true);
      await postApiCall(`${API_FLASH_MESSAGE_EDIT}/${selectedId}`, {
        message: selectedMessage,
      });
      toast.success('Message Updated Successfully');
      setViewModalVisible(false);
      setIsEditMode(false);
      fetchFlashMessage();
    } catch (error: any) {
      toast.error(friendlyApiError(error) || 'Failed to update flash message');
      console.error('Error updating message:', error);
    } finally {
      setUpdating(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.FLASH_MESSAGE,
    'read',
  );
  const aclOpsLoaded = Object.keys(moduleOperationsForReadPermissionCheck).length > 0;
  const canAdd = isSuperAdmin || loggedInUserACL[MODULE_ACL.FLASH_MESSAGE]?.add;
  const canEditFlashMessage =
    isSuperAdmin || loggedInUserACL[MODULE_ACL.FLASH_MESSAGE]?.edit;

  if (aclOpsLoaded && !canRead && !isSuperAdmin) {
    return (
      <FillPage>
        <PageHeader title="Flash Messages" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 80,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
      ),
    },
    {
      accessorKey: 'module_name',
      header: 'Module',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.module_name}</span>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.action || '—'}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      cell: ({ row }) => (
        <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
          {row.original.type}
        </span>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      cell: ({ row }) => {
        const p = Number(row.original.priority);
        const tone =
          p === 1
            ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
            : p === 2
              ? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
              : 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold tabular-nums ring-1 ring-inset ' +
              tone
            }
          >
            {row.original.priority}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'message',
      header: 'Actions',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => handleViewMessage(row.original)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
          title="View message"
          aria-label="View message"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Flash Messages"
        description="Module-scoped inline notes and flash messages shown across the app."
        actions={
          <>
            {flashMessageList.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.FLASH_MESSAGE.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Flash Message
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="flash-messages:list:v1"
        columns={columns}
        data={flashMessageList}
        isLoading={loading}
        searchPlaceholder="Search by module name…"
        emptyTitle="No flash messages"
        emptyDescription="No flash messages match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['message'] }}
        pageSize={10}
        disableExport
      />

      {/* View/Edit modal */}
      <Dialog
        open={viewModalVisible}
        onOpenChange={(o) => {
          if (!o) {
            setViewModalVisible(false);
            setIsEditMode(false);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 pr-8">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <MessageSquare className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-lg font-bold text-foreground">
                  Flash Message
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  {isEditMode
                    ? 'Edit the flash message body.'
                    : 'Read-only view of the flash message body.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={selectedMessage}
            onChange={(e) => setSelectedMessage(e.target.value)}
            rows={10}
            disabled={!isEditMode}
            className="font-mono text-xs"
          />
          <DialogFooter className="gap-2 sm:gap-2">
            {isEditMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsEditMode(false)}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateMessage} disabled={updating} className="gap-1.5">
                  {updating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Pencil className="h-3.5 w-3.5" />
                  )}
                  {updating ? 'Updating…' : 'Update'}
                </Button>
              </>
            ) : (
              <>
                {canEditFlashMessage && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditMode(true)}
                    className="gap-1.5"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                )}
                <Button onClick={() => setViewModalVisible(false)}>Close</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default FlashMessageList;
